// MARK: - Error Action Types
export enum ErrorActionType {
  RECONNECT_EXCHANGE = 'reconnect_exchange',
  RETRY = 'retry',
  WAIT_AND_RETRY = 'wait_and_retry',
  CHECK_PERMISSIONS = 'check_permissions',
  CONTACT_SUPPORT = 'contact_support',
  USE_CSV = 'use_csv',
  UPDATE_API_SETTINGS = 'update_api_settings',
  REMOVE_INTEGRATION = 'remove_integration',
}

// MARK: - Error Severity
export enum ErrorSeverity {
  LOW = 'low',      // Will resolve automatically
  MEDIUM = 'medium', // User action recommended
  HIGH = 'high',     // User action required
}

// MARK: - Exchange Error Info Model
export interface ExchangeErrorInfo {
  userMessage: string;
  userFriendlyMessage: string;
  technicalMessage: string;
  actionNeeded: ErrorActionType;
  severity: ErrorSeverity;
  isTemporary: boolean;
  actionButtonTitle?: string;
  helpText?: string;
}

// Helper to get severity color
export const getSeverityColor = (severity: ErrorSeverity): string => {
  switch (severity) {
    case ErrorSeverity.LOW:
      return 'blue';
    case ErrorSeverity.MEDIUM:
      return 'orange';
    case ErrorSeverity.HIGH:
      return 'red';
    default:
      return 'gray';
  }
};

// Helper to get display title
export const getErrorDisplayTitle = (actionNeeded: ErrorActionType): string => {
  switch (actionNeeded) {
    case ErrorActionType.RECONNECT_EXCHANGE:
      return 'API Credentials Invalid';
    case ErrorActionType.RETRY:
      return 'Sync Failed';
    case ErrorActionType.WAIT_AND_RETRY:
      return 'Rate Limited';
    case ErrorActionType.CHECK_PERMISSIONS:
      return 'Insufficient Permissions';
    case ErrorActionType.CONTACT_SUPPORT:
      return 'Technical Issue';
    case ErrorActionType.USE_CSV:
      return 'Exchange Not Supported';
    case ErrorActionType.UPDATE_API_SETTINGS:
      return 'API Settings Invalid';
    case ErrorActionType.REMOVE_INTEGRATION:
      return 'Integration No Longer Supported';
    default:
      return 'Error';
  }
};

// Helper to get error details
export const getErrorDetails = (errorInfo: ExchangeErrorInfo): string => {
  if (errorInfo.helpText) {
    return errorInfo.helpText;
  }

  switch (errorInfo.actionNeeded) {
    case ErrorActionType.RECONNECT_EXCHANGE:
      return 'API keys may have expired, been revoked, or have insufficient permissions';
    case ErrorActionType.RETRY:
      return 'This is usually a temporary issue and sync will retry automatically';
    case ErrorActionType.WAIT_AND_RETRY:
      return 'Too many requests sent to the exchange. Sync will retry in a few minutes';
    case ErrorActionType.CHECK_PERMISSIONS:
      return "Ensure your API key has 'read' permissions enabled for trading data";
    case ErrorActionType.CONTACT_SUPPORT:
      return 'This appears to be a technical issue that may require support assistance';
    case ErrorActionType.USE_CSV:
      return 'API integration is not yet available, but you can upload CSV files manually';
    case ErrorActionType.UPDATE_API_SETTINGS:
      return "Check your exchange API settings and ensure they're configured correctly";
    case ErrorActionType.REMOVE_INTEGRATION:
      return 'This integration has been retired. Remove it to clean up your tax reports.';
    default:
      return 'An error occurred';
  }
};

// MARK: - Exchange Error Mapper
class ExchangeErrorMapper {
  private static instance: ExchangeErrorMapper;

  private constructor() {}

  static getInstance(): ExchangeErrorMapper {
    if (!ExchangeErrorMapper.instance) {
      ExchangeErrorMapper.instance = new ExchangeErrorMapper();
    }
    return ExchangeErrorMapper.instance;
  }

  // MARK: - Main Error Mapping Function
  mapExchangeError(rawError?: string, exchangeName?: string): ExchangeErrorInfo {
    if (!rawError) {
      return {
        userMessage: 'Unknown error occurred',
        userFriendlyMessage: 'Unknown error occurred',
        technicalMessage: 'No error message provided',
        actionNeeded: ErrorActionType.RETRY,
        severity: ErrorSeverity.MEDIUM,
        isTemporary: false,
      };
    }

    const lowercaseError = rawError.toLowerCase();
    const exchange = exchangeName?.toLowerCase();

    // 1. Authentication & API Key Errors (Highest Priority)
    const authError = this.mapAuthenticationErrors(lowercaseError, rawError, exchange);
    if (authError) return authError;

    // 2. Rate Limiting Errors
    const rateLimitError = this.mapRateLimitErrors(lowercaseError, rawError);
    if (rateLimitError) return rateLimitError;

    // 3. Network & Connection Errors
    const networkError = this.mapNetworkErrors(lowercaseError, rawError);
    if (networkError) return networkError;

    // 4. Exchange-Specific Errors
    const exchangeError = this.mapExchangeSpecificErrors(lowercaseError, rawError, exchange);
    if (exchangeError) return exchangeError;

    // 5. Generic API & Data Errors
    const genericError = this.mapGenericErrors(lowercaseError, rawError);
    if (genericError) return genericError;

    // 6. Fallback for Unknown Errors
    return {
      userMessage: 'An unexpected error occurred. Our team has been notified.',
      userFriendlyMessage: 'An unexpected error occurred. Our team has been notified.',
      technicalMessage: rawError,
      actionNeeded: ErrorActionType.CONTACT_SUPPORT,
      severity: ErrorSeverity.MEDIUM,
      isTemporary: true,
    };
  }

  // MARK: - Authentication Errors
  private mapAuthenticationErrors(lowercaseError: string, error: string, exchange?: string): ExchangeErrorInfo | null {
    // EAPI (Exchange API) Errors
    if (lowercaseError.includes('eapi:invalid nonce')) {
      return {
        userMessage: 'API synchronization issue detected',
        userFriendlyMessage: 'Your API credentials have a timing issue. Please reconnect your exchange with new API keys.',
        technicalMessage: error,
        actionNeeded: ErrorActionType.RECONNECT_EXCHANGE,
        severity: ErrorSeverity.HIGH,
        isTemporary: false,
        actionButtonTitle: 'Reconnect Exchange',
      };
    }

    if (lowercaseError.includes('eapi:')) {
      return {
        userMessage: 'Exchange API authentication failed',
        userFriendlyMessage: 'Your API credentials are invalid or expired. Please reconnect your exchange with new API keys.',
        technicalMessage: error,
        actionNeeded: ErrorActionType.RECONNECT_EXCHANGE,
        severity: ErrorSeverity.HIGH,
        isTemporary: false,
        actionButtonTitle: 'Reconnect Exchange',
      };
    }

    // Timestamp/Nonce Errors - Common from Binance and others
    if (lowercaseError.includes('timestamp for this request is outside of the recvwindow') ||
        (lowercaseError.includes('code') && lowercaseError.includes('-1021'))) {
      return {
        userMessage: 'Request timing issue',
        userFriendlyMessage: "The request timing is outside the exchange's accepted window. This usually means API credentials need to be refreshed.",
        technicalMessage: error,
        actionNeeded: ErrorActionType.RECONNECT_EXCHANGE,
        severity: ErrorSeverity.HIGH,
        isTemporary: false,
        actionButtonTitle: 'Refresh API Keys',
      };
    }

    // Invalid Nonce (Generic)
    if (lowercaseError.includes('invalid nonce') || lowercaseError.includes('invalid-nonce')) {
      return {
        userMessage: 'API timing synchronization issue',
        userFriendlyMessage: "There's a timing issue with your API connection. Please reconnect your exchange to resolve this.",
        technicalMessage: error,
        actionNeeded: ErrorActionType.RECONNECT_EXCHANGE,
        severity: ErrorSeverity.HIGH,
        isTemporary: false,
        actionButtonTitle: 'Reconnect Exchange',
      };
    }

    // Invalid API Key Errors
    if (lowercaseError.includes('invalid api-key') ||
        lowercaseError.includes('invalid api key') ||
        (lowercaseError.includes('api-key') && lowercaseError.includes('invalid'))) {
      return {
        userMessage: 'Invalid API credentials',
        userFriendlyMessage: 'Your API key is invalid or has been revoked. Please create new API keys on your exchange and reconnect.',
        technicalMessage: error,
        actionNeeded: ErrorActionType.RECONNECT_EXCHANGE,
        severity: ErrorSeverity.HIGH,
        isTemporary: false,
        actionButtonTitle: 'Update API Keys',
      };
    }

    // Unauthorized/401 Errors
    if (lowercaseError.includes('unauthorized') || lowercaseError.includes('401')) {
      return {
        userMessage: 'Access denied',
        userFriendlyMessage: 'Your API credentials are invalid or expired. Please reconnect your exchange with valid API keys.',
        technicalMessage: error,
        actionNeeded: ErrorActionType.RECONNECT_EXCHANGE,
        severity: ErrorSeverity.HIGH,
        isTemporary: false,
        actionButtonTitle: 'Reconnect Exchange',
      };
    }

    // Forbidden/403 Errors
    if (lowercaseError.includes('forbidden') || lowercaseError.includes('403')) {
      return {
        userMessage: 'Insufficient permissions',
        userFriendlyMessage: "Your API key doesn't have the required permissions. Please ensure your API key has 'read' permissions and reconnect.",
        technicalMessage: error,
        actionNeeded: ErrorActionType.RECONNECT_EXCHANGE,
        severity: ErrorSeverity.HIGH,
        isTemporary: false,
        actionButtonTitle: 'Update Permissions',
        helpText: "Make sure your API key has 'Spot & Margin Trading' read permissions enabled.",
      };
    }

    // Authentication-error status
    if (lowercaseError.includes('authentication-error') || lowercaseError.includes('authentication failed')) {
      return {
        userMessage: 'Authentication failed',
        userFriendlyMessage: 'Your API credentials are invalid or expired. Please reconnect your exchange with valid API keys.',
        technicalMessage: error,
        actionNeeded: ErrorActionType.RECONNECT_EXCHANGE,
        severity: ErrorSeverity.HIGH,
        isTemporary: false,
        actionButtonTitle: 'Reconnect Exchange',
      };
    }

    return null;
  }

  // MARK: - Rate Limiting Errors
  private mapRateLimitErrors(lowercaseError: string, error: string): ExchangeErrorInfo | null {
    if (lowercaseError.includes('rate limit') ||
        lowercaseError.includes('too many requests') ||
        lowercaseError.includes('429')) {
      return {
        userMessage: 'Rate limit reached',
        userFriendlyMessage: 'Too many requests have been sent to the exchange. Sync will automatically retry in a few minutes.',
        technicalMessage: error,
        actionNeeded: ErrorActionType.WAIT_AND_RETRY,
        severity: ErrorSeverity.LOW,
        isTemporary: true,
        actionButtonTitle: 'Retry Later',
      };
    }

    return null;
  }

  // MARK: - Network & Connection Errors
  private mapNetworkErrors(lowercaseError: string, error: string): ExchangeErrorInfo | null {
    if (lowercaseError.includes('connection') ||
        lowercaseError.includes('timeout') ||
        lowercaseError.includes('network') ||
        lowercaseError.includes('dns') ||
        lowercaseError.includes('socket')) {
      return {
        userMessage: 'Connection issue',
        userFriendlyMessage: 'Unable to connect to the exchange. This is usually temporary and sync will retry automatically.',
        technicalMessage: error,
        actionNeeded: ErrorActionType.RETRY,
        severity: ErrorSeverity.LOW,
        isTemporary: true,
        actionButtonTitle: 'Retry Now',
      };
    }

    return null;
  }

  // MARK: - Exchange-Specific Errors
  private mapExchangeSpecificErrors(lowercaseError: string, error: string, exchange?: string): ExchangeErrorInfo | null {
    if (!exchange) return null;

    switch (exchange) {
      case 'binance':
        return this.mapBinanceErrors(lowercaseError, error);
      case 'kraken':
        return this.mapKrakenErrors(lowercaseError, error);
      case 'coinbase':
        return this.mapCoinbaseErrors(lowercaseError, error);
      case 'upbit':
        return this.mapUpbitErrors(lowercaseError, error);
      case 'coinledger':
        return this.mapRetiredIntegrationErrors(lowercaseError, error, 'CoinLedger');
      default:
        return null;
    }
  }

  private mapRetiredIntegrationErrors(lowercaseError: string, error: string, displayName: string): ExchangeErrorInfo | null {
    if (lowercaseError.includes('not-supported') || lowercaseError.includes('not supported')) {
      return {
        userMessage: `${displayName} integration retired`,
        userFriendlyMessage: `${displayName} is no longer a supported integration. Remove it to keep your tax reports accurate.`,
        technicalMessage: error,
        actionNeeded: ErrorActionType.REMOVE_INTEGRATION,
        severity: ErrorSeverity.HIGH,
        isTemporary: false,
        actionButtonTitle: 'Remove integration',
      };
    }
    return null;
  }

  private mapBinanceErrors(lowercaseError: string, error: string): ExchangeErrorInfo | null {
    if (lowercaseError.includes('code') && lowercaseError.includes('-1022')) {
      return {
        userMessage: 'Invalid signature',
        userFriendlyMessage: 'API signature is invalid. Please reconnect your Binance account with fresh API keys.',
        technicalMessage: error,
        actionNeeded: ErrorActionType.RECONNECT_EXCHANGE,
        severity: ErrorSeverity.HIGH,
        isTemporary: false,
        actionButtonTitle: 'Reconnect Binance',
      };
    }

    return null;
  }

  private mapKrakenErrors(lowercaseError: string, error: string): ExchangeErrorInfo | null {
    if (lowercaseError.includes('invalid key')) {
      return {
        userMessage: 'Invalid Kraken API key',
        userFriendlyMessage: 'Your Kraken API key is invalid. Please generate new API keys in your Kraken account and reconnect.',
        technicalMessage: error,
        actionNeeded: ErrorActionType.RECONNECT_EXCHANGE,
        severity: ErrorSeverity.HIGH,
        isTemporary: false,
        actionButtonTitle: 'Reconnect Kraken',
      };
    }

    return null;
  }

  private mapCoinbaseErrors(lowercaseError: string, error: string): ExchangeErrorInfo | null {
    if (lowercaseError.includes('invalid_grant')) {
      return {
        userMessage: 'Coinbase authorization expired',
        userFriendlyMessage: 'Your Coinbase authorization has expired. Please reconnect your Coinbase account.',
        technicalMessage: error,
        actionNeeded: ErrorActionType.RECONNECT_EXCHANGE,
        severity: ErrorSeverity.HIGH,
        isTemporary: false,
        actionButtonTitle: 'Reconnect Coinbase',
      };
    }

    return null;
  }

  private mapUpbitErrors(lowercaseError: string, error: string): ExchangeErrorInfo | null {
    if (lowercaseError.includes('not-supported') || lowercaseError.includes('not supported')) {
      return {
        userMessage: 'Upbit not yet supported',
        userFriendlyMessage: 'Upbit integration is coming soon. You can upload CSV files in the meantime.',
        technicalMessage: error,
        actionNeeded: ErrorActionType.USE_CSV,
        severity: ErrorSeverity.MEDIUM,
        isTemporary: false,
        actionButtonTitle: 'Upload CSV Instead',
      };
    }

    return null;
  }

  // MARK: - Generic Errors
  private mapGenericErrors(lowercaseError: string, error: string): ExchangeErrorInfo | null {
    // Maintenance & Service Errors
    if (lowercaseError.includes('maintenance') ||
        lowercaseError.includes('unavailable') ||
        lowercaseError.includes('service') ||
        lowercaseError.includes('502') ||
        lowercaseError.includes('503')) {
      return {
        userMessage: 'Exchange temporarily unavailable',
        userFriendlyMessage: 'The exchange is currently undergoing maintenance. Sync will automatically retry when the exchange is back online.',
        technicalMessage: error,
        actionNeeded: ErrorActionType.WAIT_AND_RETRY,
        severity: ErrorSeverity.LOW,
        isTemporary: true,
        actionButtonTitle: 'Retry Later',
      };
    }

    // Data Format Errors
    if (lowercaseError.includes('parse') ||
        lowercaseError.includes('format') ||
        lowercaseError.includes('invalid data') ||
        lowercaseError.includes('json')) {
      return {
        userMessage: 'Data format issue',
        userFriendlyMessage: 'There was an issue processing data from the exchange. This usually resolves automatically on retry.',
        technicalMessage: error,
        actionNeeded: ErrorActionType.RETRY,
        severity: ErrorSeverity.LOW,
        isTemporary: true,
        actionButtonTitle: 'Retry Sync',
      };
    }

    // Permission Errors
    if (lowercaseError.includes('permission') ||
        lowercaseError.includes('scope') ||
        lowercaseError.includes('access denied')) {
      return {
        userMessage: 'Insufficient API permissions',
        userFriendlyMessage: "Your API key doesn't have the required permissions. Please check your exchange API settings and ensure read permissions are enabled.",
        technicalMessage: error,
        actionNeeded: ErrorActionType.CHECK_PERMISSIONS,
        severity: ErrorSeverity.MEDIUM,
        isTemporary: false,
        actionButtonTitle: 'Check API Settings',
        helpText: 'Log into your exchange account and verify your API key has read permissions for trading data.',
      };
    }

    return null;
  }
}

export const exchangeErrorMapper = ExchangeErrorMapper.getInstance();
